<template>
  <modal :name="name" :width="560" height="auto" :adaptive="true" :scrollable="true" :focus-trap="true" :clickToClose="false" @before-open="initialize" @before-close="beforeClose" @closed="$emit('closed')" @opened="focusHeading">
    <div v-if="action" class="popup booking-auto-modal" :aria-busy="loadingFees || saving || reloading" @keydown.esc.prevent.stop="close">
      <button class="popup__close" type="button" :disabled="saving || reloading" :aria-label="`Close ${action.name}`" @click="close" />
      <header class="booking-auto-modal__header">
        <h2 ref="heading" class="popup__title" tabindex="-1">{{ action.name }}</h2>
        <p>{{ booking.airline.name }} · Booking #{{ booking.id }}</p>
      </header>
      <div class="popup__body">
        <section class="booking-auto-modal__explanation" aria-label="What will happen">
          <p>{{ action.explanation }}</p>
        </section>
        <template v-if="action.code === 'cancel'">
          <div v-if="loadingFees" class="booking-auto-modal__loading" role="status" aria-label="Loading airline cancellation terms"><app-spinner /></div>
          <section v-if="fee" class="booking-auto-modal__terms" aria-label="Airline cancellation terms">
            <dl v-if="fee.price !== null && fee.price !== undefined"><dt>Airline cancellation fee</dt><dd>{{ fee.price }}</dd></dl>
            <p v-if="feeText">{{ feeText }}</p>
          </section>
          <div v-if="feesUnavailable" class="booking-auto-modal__error" role="alert"><i class="pi pi-exclamation-triangle" aria-hidden="true" /><p>We could not retrieve the airline's cancellation terms. You can still cancel this booking, but any cancellation fees are unknown. Proceed at your own risk.</p></div>
        </template>
        <div v-if="error" class="booking-auto-modal__error" role="alert"><i class="pi pi-exclamation-circle" aria-hidden="true" /><p>{{ error }}</p></div>
        <div v-if="!permitted && !completed" class="booking-auto-modal__error" role="alert"><i class="pi pi-exclamation-circle" aria-hidden="true" /><p>{{ unavailableReason }}</p></div>
        <p v-if="completed" class="booking-auto-modal__success" role="status">Action completed. {{ reloading ? 'Updating booking…' : 'Reload booking details before continuing.' }}</p>
        <div v-if="uncertain" class="booking-auto-modal__error" role="alert"><i class="pi pi-exclamation-circle" aria-hidden="true" /><p>The airline result could not be verified. Reload the booking and check its status before starting another action.</p></div>
        <footer class="booking-auto-modal__footer">
          <app-button v-if="completed || uncertain" :disabled="reloading" @click.native="reloadBooking">{{ reloading ? 'Updating…' : 'Reload booking' }}</app-button>
          <app-button v-else :theme="action.code === 'cancel' ? 'error' : 'default'" :disabled="!canSubmit" @click.native="submit">{{ saving ? 'Processing…' : action.name }}</app-button>
        </footer>
      </div>
    </div>
  </modal>
</template>

<script>
import { getFlightsBooking, getFlightsChangeFees, postFlightsBookingConfirm, postFlightsBookingIssueTicket, postFlightsCancel } from '@/http'
import { bookingAutoActions } from './booking-auto-actions'
import { responseError } from './booking-detail'

const requests = { 'confirm': postFlightsBookingConfirm, 'issue-ticket': postFlightsBookingIssueTicket, 'cancel': postFlightsCancel }
const successMessages = { 'confirm': 'Booking confirmed.', 'issue-ticket': 'Tickets issued.', 'cancel': 'Booking cancelled.' }

export default {
  name: 'BookingAutoActionModal',
  props: {
    name: { type: String, required: true },
    booking: { type: Object, required: true },
    action: { type: Object, default: null }
  },
  data() {
    return { error: '', fee: null, loadingFees: false, feesUnavailable: false, saving: false, reloading: false, completed: false, uncertain: false, sessionId: 0 }
  },
  computed: {
    currentAction() { return this.action && bookingAutoActions(this.booking, this.$store.state.userGroups).find(action => action.code === this.action.code) },
    permitted() { return Boolean(this.currentAction && !this.currentAction.disabled) },
    unavailableReason() { return (this.currentAction && this.currentAction.disabledReason) || 'This action is no longer available for this booking or your role.' },
    canSubmit() { return this.permitted && !this.loadingFees && !this.saving && !this.reloading && !this.completed && !this.uncertain },
    feeText() {
      if (!this.fee || !this.fee.text) return ''
      // Airline terms can contain HTML. Show only inert text, never provider markup.
      const document = new DOMParser().parseFromString(String(this.fee.text), 'text/html')
      document.querySelectorAll('script, style').forEach(node => node.remove())
      document.querySelectorAll('br').forEach(node => node.replaceWith('\n'))
      return document.body.textContent.trim()
    }
  },
  beforeDestroy() { this.sessionId++ },
  methods: {
    async initialize() {
      this.sessionId++
      this.error = ''
      this.saving = false
      this.reloading = false
      this.completed = false
      this.uncertain = false
      this.fee = null
      this.feesUnavailable = false
      this.loadingFees = false
      if (!this.action || this.action.code !== 'cancel') return
      const sessionId = this.sessionId
      this.loadingFees = true
      try {
        const { data } = await getFlightsChangeFees(this.booking.id, { localErrors: true })
        if (sessionId !== this.sessionId) return
        const terms = data && data.cancel
        const hasTerms = terms && ((terms.price !== null && terms.price !== undefined && String(terms.price).trim() !== '') || (terms.text && String(terms.text).trim()))
        if (data && !data.error && hasTerms) this.fee = terms
        else this.feesUnavailable = true
      } catch (error) {
        if (sessionId === this.sessionId) this.feesUnavailable = true
      } finally {
        if (sessionId === this.sessionId) this.loadingFees = false
      }
    },
    async submit() {
      if (!this.canSubmit || !requests[this.action.code]) return
      const sessionId = this.sessionId
      const bookingId = this.booking.id
      const actionCode = this.action.code
      this.saving = true
      this.error = ''
      let sent = false
      try {
        // Recheck server state and current frontend role before an airline mutation.
        const { data: current } = await getFlightsBooking(bookingId, { localErrors: true })
        if (sessionId !== this.sessionId) return
        const allowed = bookingAutoActions(current, this.$store.state.userGroups).find(action => action.code === actionCode)
        if (!current || current.id !== bookingId || !allowed || allowed.disabled) {
          if (current && current.id === bookingId) this.$emit('updated', current)
          this.error = (allowed && allowed.disabledReason) || 'The booking changed or this action is no longer available. Reload the booking before continuing.'
          return
        }
        sent = true
        const { data } = await requests[actionCode](bookingId, { localErrors: true })
        if (sessionId !== this.sessionId) return
        this.completed = true
        this.$notify({ type: 'success', title: successMessages[actionCode] })
        const status = data && ((data.booking && data.booking.status) || data.status)
        if (status) this.$emit('status-change', { id: bookingId, status })
        await this.reloadBooking()
      } catch (error) {
        if (sessionId === this.sessionId) {
          this.$notify({ type: 'error', title: responseError(error), duration: 8000 })
          this.uncertain = sent && (!error.response || error.response.status >= 500)
        }
      } finally {
        if (sessionId === this.sessionId) this.saving = false
      }
    },
    async reloadBooking() {
      if (this.reloading) return
      const sessionId = this.sessionId
      const bookingId = this.booking.id
      this.reloading = true
      this.error = ''
      try {
        const { data } = await getFlightsBooking(bookingId, { localErrors: true })
        if (sessionId !== this.sessionId) return
        if (!data || data.id !== bookingId) throw new Error('Invalid booking response')
        this.$emit('updated', data)
        this.reloading = false
        this.saving = false
        this.close()
      } catch (error) {
        if (sessionId === this.sessionId) this.$notify({ type: 'error', title: responseError(error, 'Unable to reload booking details.'), duration: 8000 })
      } finally {
        if (sessionId === this.sessionId) this.reloading = false
      }
    },
    focusHeading() { if (this.$refs.heading) this.$refs.heading.focus() },
    close() { if (!this.saving && !this.reloading) this.$modal.hide(this.name) },
    beforeClose(event) {
      if (this.saving || this.reloading) event.cancel()
      else this.sessionId++
    }
  }
}
</script>

<style lang="scss" scoped>
.booking-auto-modal { color: #20241d; font: 400 15px/1.5 $font-family-base; }
.booking-auto-modal__header { padding-right: 32px; margin-bottom: 20px; }
.booking-auto-modal .popup__title { margin-bottom: 6px; font-size: 20px; font-weight: 500; text-align: left; cursor: default; }
.booking-auto-modal__header > p { margin: 0; color: $color-text-second; font-size: 13px; }
.booking-auto-modal__explanation { padding: 16px; border: 1px solid $color-border; border-radius: 6px; background: #f7f8f5; }
.booking-auto-modal__explanation p { margin: 0; }
.booking-auto-modal__loading { position: relative; min-height: 100px; }
.booking-auto-modal__terms { margin-top: 20px; }
.booking-auto-modal__terms dl { display: flex; flex-wrap: wrap; justify-content: space-between; gap: 8px 16px; margin: 0; padding-bottom: 12px; border-bottom: 1px solid $color-border; }
.booking-auto-modal__terms dt { color: $color-text-second; }
.booking-auto-modal__terms dd { margin: 0; font-weight: 500; }
.booking-auto-modal__terms p { white-space: pre-line; overflow-wrap: anywhere; }
.booking-auto-modal__error { display: flex; align-items: flex-start; gap: 10px; margin-top: 16px; padding: 14px; border: 1px solid #efd4d1; border-radius: 6px; background: #fff5f3; color: #993c35; font-size: 14px; line-height: 1.5; overflow-wrap: anywhere; }
.booking-auto-modal__error > .pi { flex-shrink: 0; margin-top: 2px; font-size: 18px; }
.booking-auto-modal__error > p { min-width: 0; }
.booking-auto-modal__error p { margin: 0; }
.booking-auto-modal__success { color: #527b12; }
.booking-auto-modal__footer { display: flex; justify-content: flex-end; flex-wrap: wrap; gap: 12px; margin-top: 24px; padding-top: 18px; border-top: 1px solid $color-border; }
.booking-auto-modal__footer .btn { min-width: auto; }
.booking-auto-modal .popup__close:focus-visible { outline: 2px solid $color-theme; outline-offset: 3px; }
@media (max-width: 520px) { .booking-auto-modal__footer { gap: 8px; } }
</style>
